import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createBuilding } from "../../../api/building";
import { Building } from "../../../generated/types";
import { Button } from "../../common/Button";
import {
  BuildingFields,
  EMPTY_BUILDING_FORM,
  toCreateBuilding,
  useBuildingForm,
} from "../building/BuildingFields";
import { SetupError } from "./SetupLayout";

/**
 * Bâtiments déjà créés dans l'élevage, suivis du formulaire pour en ajouter un autre.
 * Partagé par la mise en route de l'éleveur et la création d'élevage côté admin / PSC.
 */
export const AddBuildingForm = ({
  breedingId,
  buildings,
  onAdded,
}: {
  breedingId: number;
  buildings: Building[];
  onAdded: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const buildingForm = useBuildingForm(EMPTY_BUILDING_FORM);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!buildingForm.validate()) {
      return;
    }

    setSubmitting(true);
    // Les produits sont rattachés à l'étape suivante : un régulateur legacy exige un
    // bâtiment pour être associé, il ne peut donc pas exister avant lui.
    const result = await createBuilding(
      toCreateBuilding(buildingForm.form, breedingId)
    );
    setSubmitting(false);

    if (!result.success) {
      setError(result.error || t("setup-error-building"));
      return;
    }
    buildingForm.reset(EMPTY_BUILDING_FORM);
    await onAdded();
  };

  return (
    <>
      {buildings.length > 0 && (
        <ul className="mb-6 flex flex-col gap-2">
          {buildings.map((building) => (
            <li
              key={building.id}
              className="flex items-center gap-3 bg-grey-light/50 rounded-lg px-4 py-2 text-default-text"
            >
              <FontAwesomeIcon icon="check-circle" />
              <span className="font-oxaniumMedium">{building.name}</span>
              <span className="text-sm text-grey-readable">
                {building.inuav}
              </span>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="space-y-4">
        <SetupError message={error} />

        <BuildingFields buildingForm={buildingForm} />

        <Button
          type="submit"
          variant="secondary"
          size="full"
          disabled={submitting}
        >
          {submitting ? t("setup-saving") : t("setup-buildings-add")}
        </Button>
      </form>
    </>
  );
};
